import { useState, useEffect, useCallback, useRef } from "react";
import { getConfigsOperationalActivities } from "@/apis/activity";

/**
 * 活动配置Hook
 * 用于管理和获取运营活动配置
 * @returns {
 *   activityConfig: 当前活动配置,
 *   initActivityConfig: 初始化活动配置的方法
 * }
 */
export const useActivityConfig = () => {
  const [activityConfig, setActivityConfig] = useState<any>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // 初始化活动配置的方法
  const initActivityConfig = useCallback(async() => {
    // 清除之前的定时器
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    const res = await getConfigsOperationalActivities();
    console.log('获取到的活动配置:', res);
    const config = res || {}
    // 直接更新状态，不存储到本地
    setActivityConfig(config);

    // 触发自定义事件，通知页面配置已更新
    window.dispatchEvent(
      new CustomEvent("activityConfigUpdated", {
        detail: config,
      }),
    );

    if (config) {
      const now = Date.now();
      const expiryTimes = [];

      console.log('当前时间:', new Date(now).toLocaleString());

      // 收集所有活动的过期时间
      Object.keys(config).forEach((key) => {
        const activity = config[key];
        if (activity.use_config && activity.expired_at) {
          const expiredAt = new Date(activity.expired_at).getTime();
          console.log(`${key} 过期时间:`, new Date(expiredAt).toLocaleString());
          console.log(`${key} 过期时间戳:`, expiredAt);
          console.log(`${key} 当前时间戳:`, now);
          console.log(`${key} 是否有效:`, !isNaN(expiredAt));
          console.log(`${key} 是否未过期:`, now < expiredAt);
          // 添加日期有效性检查
          if (!isNaN(expiredAt) && now < expiredAt) {
            expiryTimes.push(expiredAt);
            console.log(`${key} 添加到过期时间列表`);
          }
        }
      });

      console.log('过期时间列表:', expiryTimes);

      // 只设置最近的一个定时器
      if (expiryTimes.length > 0) {
        const nextExpiry = Math.min(...expiryTimes);
        const timeUntilExpiry = nextExpiry - now;
        console.log('最近的过期时间:', new Date(nextExpiry).toLocaleString());
        console.log('距离过期时间:', timeUntilExpiry, '毫秒');
        console.log('距离过期时间:', timeUntilExpiry / (1000 * 60 * 60), '小时');

        // 处理setTimeout最大值限制（约24.8天）
        const MAX_TIMEOUT = 2147483647; // setTimeout的最大值
        if (timeUntilExpiry > MAX_TIMEOUT) {
          console.log('时间差超过setTimeout最大值，使用24小时轮询');
          // 使用24小时轮询代替直接设置长定时器
          timerRef.current = setTimeout(() => {
            console.log('轮询检查活动状态');
            // 重新调用initConfig检查活动状态
            if (activityConfig) {
              const updatedConfig = { ...activityConfig };
              let hasExpired = false;
              // 遍历所有活动，检查是否已过期
              Object.keys(updatedConfig).forEach((key) => {
                const activity = updatedConfig[key];
                if (activity.use_config && activity.expired_at) {
                  const expiredAt = new Date(activity.expired_at).getTime();
                  if (Date.now() >= expiredAt) {
                    // 标记活动为已过期
                    updatedConfig[key] = { ...activity, use_config: false };
                    hasExpired = true;
                  }
                }
              });
              if (hasExpired) {
                setActivityConfig(updatedConfig);
                // 触发自定义事件，通知页面配置已更新
                window.dispatchEvent(
                  new CustomEvent("activityConfigUpdated", {
                    detail: updatedConfig,
                  }),
                );
              }
            }
            // 重新设置轮询定时器
            initActivityConfig();
          }, 24 * 60 * 60 * 1000); // 24小时
        } else {
          // 正常设置定时器
          timerRef.current = setTimeout(() => {
            console.log("=== 活动已结束，更新活动状态 ===");
            console.log("当前时间:", new Date().toLocaleString());
            // 活动结束时，更新activityConfig状态，标记活动为已结束
            if (activityConfig) {
              const updatedConfig = { ...activityConfig };
              let hasExpired = false;
              // 遍历所有活动，检查是否已过期
              Object.keys(updatedConfig).forEach((key) => {
                const activity = updatedConfig[key];
                if (activity.use_config && activity.expired_at) {
                  const expiredAt = new Date(activity.expired_at).getTime();
                  if (Date.now() >= expiredAt) {
                    // 标记活动为已过期
                    updatedConfig[key] = { ...activity, use_config: false };
                    hasExpired = true;
                    console.log(`活动 ${key} 已过期`);
                  }
                }
              });
              if (hasExpired) {
                setActivityConfig(updatedConfig);
                // 触发自定义事件，通知页面配置已更新
                window.dispatchEvent(
                  new CustomEvent("activityConfigUpdated", {
                    detail: updatedConfig,
                  }),
                );
                console.log("活动配置已更新");
              }
            }
            // 重新调用initConfig，为下一个活动设置定时器
            console.log("重新调用initConfig，为下一个活动设置定时器");
            initActivityConfig();
          }, timeUntilExpiry);
        }
      } else {
        console.log('没有未过期的活动，不设置定时器');
      }
    }
  }, []);

  useEffect(() => {
    // 监听配置更新事件
    const handleConfigUpdate = (event: CustomEvent) => {
      setActivityConfig(event.detail);
    };

    // 添加事件监听器
    window.addEventListener(
      "activityConfigUpdated",
      handleConfigUpdate as EventListener,
    );

    // 初始化活动配置
    initActivityConfig();

    // 清理事件监听器和定时器
    return () => {
      window.removeEventListener(
        "activityConfigUpdated",
        handleConfigUpdate as EventListener,
      );
      // 清除定时器
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [initActivityConfig]);

  return {
    activityConfig,
    initActivityConfig,
  };
};
